<template>
  <SideSheet
    class="recurring-limit"
    data-testid="recurring-limit"
    position="right"
    :is-open="isOpen"
    @close="$emit('close')"
  >
    <NavBar sticky>
      <template #left>
        <BackButton icon="EF0030" @click="$emit('close')" />
      </template>
    </NavBar>

    <section class="recurring-limit__content">
      <Heading class="recurring-limit__title" :level="2">
        {{ data.title }}
      </Heading>
      <BaseText size="p4" class="recurring-limit__description">
        {{ data.description }}
      </BaseText>

      <section>
        <ul>
          <li
            v-for="(item, index) in data.details"
            :key="index"
            class="recurring-limit__details"
          >
            <BaseText>
              <b class="recurring-limit__details-title">{{ item.title }}</b>
              {{ item.text }}
            </BaseText>
          </li>
        </ul>
      </section>
    </section>
    <ButtonContainer class="recurring-limit__button-container">
      <template #button-primary>
        <ButtonPrimary
          size="large"
          label="Confirmar recorrência"
          :loading="isLoading"
          data-testid="confirm-recurring-limit"
          @click="fetchUpdate"
        />
      </template>
      <template #button-secondary>
        <ButtonSecondary
          size="large"
          label="Cancelar"
          @click="$emit('close')"
        />
      </template>
    </ButtonContainer>
  </SideSheet>
</template>

<script lang="ts" setup>
import { computed, inject, ref, Ref, watch } from 'vue';
import { removeBodyScroll } from '@/modules/common/helpers/browser';

import {
  NavBar,
  Heading,
  BaseText,
  ButtonContainer,
  ButtonPrimary,
  ButtonSecondary
} from '@warrenbrasil/nebraska-web';
import { SideSheet } from '@/modules/common/components/SideSheet';
import { BackButton } from '@/modules/common/components/buttons';

import { IMarginAllocationV2 } from '@/modules/trade/services/margin-allocation';

interface EmitEvents {
  (event: 'close'): void;
}
const emit = defineEmits<EmitEvents>();

interface Props {
  isOpen: boolean;
}
const props = defineProps<Props>();

const updatePreference = inject('fetchUpdatePreference', async () => ({}));
const marginAllocation = inject('marginAllocation') as Ref<IMarginAllocationV2>;

const isLoading = ref(false);

const data = computed(() => {
  return marginAllocation.value.recurringLimit;
});

async function fetchUpdate() {
  try {
    isLoading.value = true;

    await updatePreference();
  } finally {
    isLoading.value = false;
    emit('close');
  }
}

watch(
  () => props.isOpen,
  _isOpen => {
    removeBodyScroll();
  }
);
</script>

<style lang="less" scoped>
.recurring-limit {
  height: 100vh;

  .navbar-container {
    border-bottom: none;
  }

  :deep(.panel-content.right) {
    width: 683px;
  }

  &__content {
    padding: @size-spacing-x800 @size-spacing-x1200;
  }

  &__title {
    margin-bottom: @size-spacing-x800;
  }

  &__description {
    margin-bottom: @size-spacing-x800;
  }

  &__details {
    margin-bottom: @size-spacing-x600;

    &-title {
      color: @element-primary;
    }
  }

  &__button-container {
    background-color: @background-secondary;
    width: 683px;
    position: fixed;
    bottom: 0;
    right: 0;
    z-index: 20;
  }
}
</style>
